import { useState, useEffect, useRef } from 'react'
import { Search, Filter, Download, Eye, EyeOff, ZoomIn, ZoomOut, BarChart3, Upload, Database } from 'lucide-react'
import DataImport from '../DataImport'

export default function VisualizationControls({
  filters,
  onFiltersChange,
  sectors = [],
  statistics = {},
  onToggleAll,
  showAllVisible,
  onZoomIn,
  onZoomOut,
  onExport,
  onDataImported
}) {
  const [showFilters, setShowFilters] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [searchInput, setSearchInput] = useState(filters.search || '')
  const debounceTimer = useRef(null)

  useEffect(() => {
    setSearchInput(filters.search || '')
  }, [filters.search])

  const handleSearchChange = (value) => {
    setSearchInput(value)

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    // Set new timer for debounced search
    debounceTimer.current = setTimeout(() => {
      onFiltersChange(prev => ({ ...prev, search: value }))
    }, 300)
  }

  const handleSectorToggle = (sector) => {
    onFiltersChange(prev => ({
      ...prev,
      sectors: prev.sectors.includes(sector)
        ? prev.sectors.filter(s => s !== sector)
        : [...prev.sectors, sector]
    }))
  }

  const handleLevelToggle = (level) => {
    onFiltersChange(prev => ({
      ...prev,
      levels: prev.levels.includes(level)
        ? prev.levels.filter(l => l !== level)
        : [...prev.levels, level]
    }))
  }

  const handleToggleCompetences = () => {
    onFiltersChange(prev => ({ ...prev, showCompetences: !prev.showCompetences }))
  }

  const clearFilters = () => {
    onFiltersChange({
      search: '',
      sectors: [],
      levels: [],
      showCompetences: filters.showCompetences
    })
  }

  const levels = ['III', 'IV', 'V', 'VI', 'VII', 'VIII']

  return (
    <div className="bg-white border-b border-gray-200 p-4 space-y-4">
      {/* Première ligne : Recherche et actions principales */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Recherche */}
        <div className="flex-1 min-w-64">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une certification, bloc ou compétence..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>
        </div>

        {/* Boutons de contrôle */}
        <div className="flex items-center gap-2">
          {/* Toggle filtres */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn ${showFilters ? 'btn-primary' : 'btn-outline'}`}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtres
          </button>

          {/* Toggle compétences */}
          <button
            onClick={handleToggleCompetences}
            className={`btn ${filters.showCompetences ? 'btn-primary' : 'btn-outline'}`}
          >
            <Eye className="h-4 w-4 mr-2" />
            Compétences
          </button>

          {/* Contrôle du nombre de compétences par bloc (visible uniquement si showCompetences) */}
          {filters.showCompetences && (
            <div className="flex items-center gap-2 text-sm">
              <label htmlFor="maxCompetences" className="text-gray-600 whitespace-nowrap">
                Max par bloc:
              </label>
              <select
                id="maxCompetences"
                value={filters.maxCompetencesPerBlock || 5}
                onChange={(e) => onFiltersChange(prev => ({ ...prev, maxCompetencesPerBlock: parseInt(e.target.value) }))}
                className="border rounded px-2 py-1 text-sm bg-white"
              >
                <option value="3">3</option>
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="999">Toutes</option>
              </select>
            </div>
          )}

          {/* Toggle all certifications */}
          <button
            onClick={onToggleAll}
            className="btn btn-outline"
            title={showAllVisible ? 'Masquer toutes les certifications' : 'Afficher toutes les certifications'}
          >
            {showAllVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>

          {/* Contrôles de zoom */}
          <div className="flex border rounded-md">
            <button
              onClick={onZoomOut}
              className="p-2 hover:bg-gray-50 border-r"
              title="Zoom arrière"
            >
              <ZoomOut className="h-4 w-4" />
            </button>
            <button
              onClick={onZoomIn}
              className="p-2 hover:bg-gray-50"
              title="Zoom avant"
            >
              <ZoomIn className="h-4 w-4" />
            </button>
          </div>

          {/* Import */}
          <button
            onClick={() => setShowImport(!showImport)}
            className={`btn ${showImport ? 'btn-primary' : 'btn-outline'}`}
          >
            <Upload className="h-4 w-4 mr-2" />
            Import
          </button>

          {/* Export */}
          <div className="relative group">
            <button className="btn btn-outline">
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
            <div className="absolute right-0 top-full mt-1 bg-white border rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <div className="py-1">
                <button
                  onClick={() => onExport('csv')}
                  className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                >
                  Export CSV
                </button>
                <button
                  onClick={() => onExport('json')}
                  className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                >
                  Export JSON
                </button>
                <button
                  onClick={() => onExport('xlsx')}
                  className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                >
                  Export Excel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="flex items-center gap-6 text-sm text-gray-600">
        <div className="flex items-center">
          <BarChart3 className="h-4 w-4 mr-1" />
          <span className="font-medium">{statistics.certificationsCount || 0}</span> certifications
        </div>
        <div>
          <span className="font-medium">{statistics.blocksCount || 0}</span> blocs
        </div>
        <div>
          <span className="font-medium">{statistics.competencesCount || 0}</span> compétences
        </div>
        {(filters.sectors.length > 0 || filters.levels.length > 0 || filters.search) && (
          <button
            onClick={clearFilters}
            className="text-secondary-600 hover:text-secondary-700 text-sm font-medium"
          >
            Effacer les filtres
          </button>
        )}
      </div>

      {/* Filtres détaillés */}
      {showFilters && (
        <div className="border-t pt-4 space-y-4">
          {/* Secteurs */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">Secteurs</h4>
            <div className="flex flex-wrap gap-2">
              {sectors.map(sector => (
                <button
                  key={sector}
                  onClick={() => handleSectorToggle(sector)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    filters.sectors.includes(sector)
                      ? 'bg-secondary-100 text-secondary-700 border-secondary-300'
                      : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {sector}
                </button>
              ))}
              {sectors.length === 0 && (
                <span className="text-sm text-gray-500 italic">Aucun secteur disponible</span>
              )}
            </div>
          </div>

          {/* Niveaux */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">Niveaux</h4>
            <div className="flex flex-wrap gap-2">
              {levels.map(level => (
                <button
                  key={level}
                  onClick={() => handleLevelToggle(level)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    filters.levels.includes(level)
                      ? 'bg-blue-100 text-blue-700 border-blue-300'
                      : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  Niveau {level}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal d'import */}
      {showImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Import de données RNCP</h3>
              <button
                onClick={() => setShowImport(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Fermer</span>
                ×
              </button>
            </div>
            <DataImport
              onImportSuccess={(result) => {
                setShowImport(false)
                if (onDataImported) {
                  onDataImported(result)
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}